import React, {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import {List, Set} from 'immutable';
import './facet-filter.scss';
import {debounce} from 'lodash';
import useOnClickOutside from 'lib/hooks/useOnClickOutside';
import {Manager, Target, Popper} from 'react-popper';
import {
  addToast,
  Button,
  Card,
  DropdownItem,
  SearchInput,
  Text,
  Icon,
  IconButton,
  LoadingSpinner
} from '@albert-io/atomic';
import {pluralize} from 'lib/stringUtils';
import classnames from 'classnames';

type OnSelectType<T> = React.Dispatch<React.SetStateAction<Set<T>>>;

interface FacetFilterProps<ItemType> {
  data?: List<ItemType>; // TODO  query or data typing
  searchQuery: (searchString: string) => any;
  selectedItems?: Set<ItemType>;
  placeHolder?: string;
  onSelect?: OnSelectType<ItemType>;
  onSelectTrack?: (item: ItemType) => void;
  onSearchTrack?: (search: string) => void;
  content?: (item: ItemType) => any;
  onApply?: (selectedItems: Set<ItemType>) => any; // todo: Remove
  onCancel?: () => any;
  buttonText?: string;
  searchText?: string;
  helpText?: string;
  onClear?: () => void;
}

// TODO: conditional query props
/*  if (!this.state.isQuerySearch) {
      const filterWithString = this.props.searchFilter(this.state.searchString);
      searchResults = this.state.data.filter(filterWithString);
    } */

const defaultContent = (item: any) => {
  if (typeof item === 'string') {
    return item;
  }
  if (typeof item.getName === 'function') {
    return item.getName();
  }
  console.warn('Must pass custom content func to render correctly');
  return null;
};

const FacetFilter = <Item extends any>({
  searchQuery,
  selectedItems = Set(),
  data,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onApply = () => {},
  onCancel = () => {},
  onSelect = () => {},
  onSelectTrack = () => {},
  onSearchTrack = () => {},
  onClear = () => {},
  buttonText = 'Item',
  placeHolder = `Choose ${pluralize(buttonText)}`,
  searchText = `Type to find ${pluralize(buttonText)}`,
  content = defaultContent,
  ...resultProps
}: FacetFilterProps<Item>) => {
  const [showPopup, toggleShowPopup] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [results, setResults] = useState<List<Item> | null>(data || null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    setIsLoading(true);
    if (searchString.length === 0) {
      setResults(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const fetchedData = await searchQuery(searchString).getResourcePromise();
      setResults(fetchedData);
    } catch (_err) {
      // TODO: determine error state, handling
      addToast({
        type: 'danger',
        title: 'error',
        message: 'There was an error loading results'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetch = useMemo(() => debounce(fetchData, 300), [searchQuery, searchString]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    debouncedFetch();
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  const isSelected = useCallback(
    (item: Item, items: Set<Item> = selectedItems) => items.includes(item),
    [selectedItems]
  );

  const selectHandler = useCallback(
    (item: Item) => {
      onSelect((prevSelected) => {
        const shouldRemove = isSelected(item, prevSelected);
        const updatedSelected = shouldRemove ? prevSelected.delete(item) : prevSelected.add(item);
        return updatedSelected;
      });
      onSelectTrack(item);
    },
    [onSelect, isSelected, onSelectTrack]
  );

  const cancelHandler = useCallback(() => {
    toggleShowPopup(false);
    onCancel();
  }, [toggleShowPopup, onCancel]);

  const doneHandler = useCallback(() => {
    toggleShowPopup(false);
    setSearchString('');
  }, [setSearchString, toggleShowPopup]);

  const btnText = useMemo(() => {
    if (selectedItems.size === 0) {
      return placeHolder;
    }
    return `${selectedItems.size} ${selectedItems.size > 1 ? pluralize(buttonText) : buttonText}`;
  }, [selectedItems, placeHolder, buttonText]);

  const emptyState = useMemo(() => {
    return searchString.length === 0 && selectedItems.size === 0;
  }, [searchString, selectedItems]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(
    dropdownRef,
    useCallback(() => !!dropdownRef && cancelHandler(), [dropdownRef, cancelHandler])
  );

  const hasResults = useMemo(() => selectedItems.size > 0, [selectedItems]);

  return (
    <div ref={dropdownRef}>
      <Manager>
        <Target>
          <div
            className={classnames('u-display_flex u-bgc_transparent u-border-radius_1', {
              'facet-filter__button-wrapper--active': hasResults
            })}
          >
            <Button
              // variant='outlined'
              variant={hasResults ? undefined : 'outlined'}
              color={hasResults ? 'inherit' : 'secondary'}
              onClick={() => toggleShowPopup((prev) => !prev)}
            >
              {btnText}
              <Icon className='u-mar-l_1' icon='caret-down' />
            </Button>
            {hasResults && (
              <IconButton color='inherit' icon='times' label='clear filter' onClick={onClear} />
            )}
          </div>
        </Target>
        {showPopup && (
          <Popper placement='bottom-start' className='facet_filter__popper'>
            <Card className='facet-filter__container'>
              {/* @TODO: make own search filter stylings */}
              <SearchInput
                variant
                placeholder={searchText}
                className='u-mar_1 facet-filter__input'
                value={searchString}
                onChange={(e) => {
                  setSearchString(e.target.value);
                }}
                onBlur={() => searchString.length > 0 && onSearchTrack(searchString)}
                onReset={() => setSearchString('')}
              />
              <FilterResults<Item>
                results={results}
                onClick={selectHandler}
                checkIsSelected={isSelected}
                content={content}
                emptyState={emptyState}
                selectedItems={selectedItems}
                isLoading={isLoading}
                {...resultProps}
              />
              {!emptyState && (
                <div className='facet_filter__button-bar u-pad_1'>
                  <Button size='s' variant='text' onClick={doneHandler}>
                    Done
                  </Button>
                </div>
              )}
            </Card>
          </Popper>
        )}
      </Manager>
    </div>
  );
};

interface FilterResultsProps<T> {
  results: List<T> | null;
  onClick: (item: T) => any;
  checkIsSelected: (item: T) => boolean;
  content?: (item: T) => any;
  emptyState?: boolean;
  selectedItems?: Set<T>;
  helpText?: string;
  isLoading?: boolean;
}

const FilterResults = <T extends any>({
  results,
  onClick,
  checkIsSelected,
  content = defaultContent,
  emptyState = false,
  selectedItems = Set(),
  isLoading = false,
  helpText = 'Insert nice description about filter'
}: FilterResultsProps<T>) => {
  const hasResults = results !== null && !results.isEmpty();

  if (isLoading && !emptyState) {
    return (
      <div className='facet_filter__loading-container u-pad_2 u-color_slate-400'>
        <LoadingSpinner size={2} />
      </div>
    );
  }

  return (
    <div className='facet-filter__results-container'>
      {selectedItems.map((selectedResult) => {
        return (
          <DropdownItem
            bold={false}
            textProps={{size: 's'}}
            checkboxProps={{size: 's'}}
            className='u-pad_1 u-display_flex u-justify-content_start u-width_100pc'
            onCheckboxChange={() => {}} // need this prop to render checkbox
            onClick={() => onClick(selectedResult as T)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                onClick(selectedResult as T);
              }
            }}
            overflowHidden
            checked
          >
            {content(selectedResult as T)}
          </DropdownItem>
        );
      })}
      {!hasResults && (
        <div className='u-pad-tb_3 u-display_flex u-justify-content_center'>
          {emptyState ? (
            <Text as='p' size='m' emphasis='lowest' className='facet_filter__dropdown-item-text'>
              {helpText}
            </Text>
          ) : (
            <>
              {results === null ? null : (
                <Text as='p' size='m' className='facet_filter__dropdown-item-text'>
                  No results found
                </Text>
              )}
            </>
          )}
        </div>
      )}

      {hasResults && emptyState && (
        <div className='u-pad-tb_3 u-display_flex u-justify-content_center'>
          <Text as='p' size='m' emphasis='lowest' className='facet_filter__dropdown-item-text'>
            {helpText}
          </Text>
        </div>
      )}

      {!emptyState &&
        hasResults &&
        results!
          .filter((r) => !checkIsSelected(r!))
          .map((result) => {
            return (
              <DropdownItem
                bold={false}
                checked={false}
                textProps={{size: 's'}}
                checkboxProps={{size: 's'}}
                className='u-pad_1 u-display_flex u-justify-content_start u-width_100pc'
                onClick={() => onClick(result as T)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    onClick(result as T);
                  }
                }}
                onCheckboxChange={() => {}} // need this prop to render checkbox
                overflowHidden
              >
                {content(result as T)}
              </DropdownItem>
            );
          })}
    </div>
  );
};

export default FacetFilter;
